const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const formatterCache = new Map();

export function isLeapYear(year) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

export function daysInMonth(year, month) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return 0;
  }
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

export function parseDateOnly(value) {
  const match = DATE_ONLY_PATTERN.exec(String(value || "").trim());
  if (!match) return null;
  const date = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3])
  };
  if (
    date.year < 1
    || date.year > 9999
    || date.month < 1
    || date.month > 12
    || date.day < 1
    || date.day > daysInMonth(date.year, date.month)
  ) return null;
  return date;
}

export function formatDateOnly(date) {
  if (!date || !parseDateOnly(
    `${pad(date.year, 4)}-${pad(date.month, 2)}-${pad(date.day, 2)}`
  )) return "";
  return `${pad(date.year, 4)}-${pad(date.month, 2)}-${pad(date.day, 2)}`;
}

export function compareDateOnly(left, right) {
  const leftOrdinal = dateOnlyToOrdinal(left);
  const rightOrdinal = dateOnlyToOrdinal(right);
  return Math.sign(leftOrdinal - rightOrdinal);
}

export function dateOnlyDistance(left, right) {
  return dateOnlyToOrdinal(left) - dateOnlyToOrdinal(right);
}

export function addDateOnlyDays(date, amount) {
  if (!Number.isInteger(amount)) throw new TypeError("Date movement must use whole days.");
  return ordinalToDateOnly(dateOnlyToOrdinal(date) + amount);
}

export function addDateOnlyMonths(date, amount) {
  if (!Number.isInteger(amount)) throw new TypeError("Date movement must use whole months.");
  const monthIndex = date.year * 12 + date.month - 1 + amount;
  const year = floorDivide(monthIndex, 12);
  const month = modulo(monthIndex, 12) + 1;
  if (year < 1 || year > 9999) return { ...date };
  return { year, month, day: Math.min(date.day, daysInMonth(year, month)) };
}

export function addDateOnlyYears(date, amount) {
  if (!Number.isInteger(amount)) throw new TypeError("Date movement must use whole years.");
  const year = date.year + amount;
  if (year < 1 || year > 9999) return { ...date };
  return { year, month: date.month, day: Math.min(date.day, daysInMonth(year, date.month)) };
}

export function dayOfWeek(date) {
  // 1970-01-01 was a Thursday. Sunday is zero to match the calendar header.
  return modulo(dateOnlyToOrdinal(date) + 4, 7);
}

export function createCalendarGrid(displayDate, selectedDate = null, today = null) {
  const first = { year: displayDate.year, month: displayDate.month, day: 1 };
  const start = addDateOnlyDays(first, -dayOfWeek(first));
  const selected = formatDateOnly(selectedDate);
  const todayValue = formatDateOnly(today);
  return Array.from({ length: 42 }, (_, index) => {
    const date = addDateOnlyDays(start, index);
    const value = formatDateOnly(date);
    return {
      date,
      value,
      inMonth: date.year === displayDate.year && date.month === displayDate.month,
      selected: value === selected,
      today: value === todayValue
    };
  });
}

export function moveCalendarDate(date, key, { shiftKey = false } = {}) {
  if (key === "ArrowLeft") return addDateOnlyDays(date, -1);
  if (key === "ArrowRight") return addDateOnlyDays(date, 1);
  if (key === "ArrowUp") return addDateOnlyDays(date, -7);
  if (key === "ArrowDown") return addDateOnlyDays(date, 7);
  if (key === "PageUp") return shiftKey
    ? addDateOnlyYears(date, -1)
    : addDateOnlyMonths(date, -1);
  if (key === "PageDown") return shiftKey
    ? addDateOnlyYears(date, 1)
    : addDateOnlyMonths(date, 1);
  if (key === "Home") return addDateOnlyDays(date, -dayOfWeek(date));
  if (key === "End") return addDateOnlyDays(date, 6 - dayOfWeek(date));
  return null;
}

export function todayDateOnly(timeZone = "UTC", timestamp = Date.now()) {
  return timestampToDateOnly(timestamp, timeZone);
}

export function timestampToDateOnly(timestamp, timeZone = "UTC") {
  if (!Number.isFinite(Number(timestamp))) return null;
  const formatter = datePartsFormatter(timeZone);
  const parts = Object.fromEntries(
    formatter.formatToParts(new Date(Number(timestamp)))
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );
  return parseDateOnly(`${parts.year}-${parts.month}-${parts.day}`);
}

export function formatDateOnlyLabel(date, locale = "en-US") {
  const value = formatDateOnly(date);
  if (!value) return "";
  const instant = new Date(0);
  instant.setUTCFullYear(date.year, date.month - 1, date.day);
  instant.setUTCHours(12, 0, 0, 0);
  return new Intl.DateTimeFormat(locale, {
    timeZone: "UTC",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  }).format(instant);
}

export function initialDateForViewport(timestamps, viewport, timeZone, fallbackDate) {
  const length = Number(timestamps?.length) || 0;
  if (length > 0 && viewport) {
    const center = Math.round(Number(viewport.start) + Number(viewport.visibleCount) / 2 - 0.5);
    const centerDate = timestampToDateOnly(timestamps[clamp(center, 0, length - 1)], timeZone);
    if (centerDate) return centerDate;

    const right = clamp(
      Math.floor(Number(viewport.start) + Number(viewport.visibleCount) - 0.5),
      0,
      length - 1
    );
    const left = clamp(Math.floor(Number(viewport.start)), 0, right);
    for (let index = right; index >= left; index -= 1) {
      const visibleDate = timestampToDateOnly(timestamps[index], timeZone);
      if (visibleDate) return visibleDate;
    }
  }
  return parseDateOnly(formatDateOnly(fallbackDate)) || todayDateOnly(timeZone);
}

export function resolveBarForDate(timestamps, requestedDate, timeframe, timeZone) {
  const requested = parseDateOnly(formatDateOnly(requestedDate));
  const length = Number(timestamps?.length) || 0;
  if (!requested || length === 0) return null;

  const requestedValue = formatDateOnly(requested);
  const intervalKind = dateIntervalKind(timeframe);
  let firstAfter = -1;
  let lastBefore = -1;

  for (let index = 0; index < length; index += 1) {
    const barDate = timestampToDateOnly(timestamps[index], timeZone);
    if (!barDate) continue;
    const barValue = formatDateOnly(barDate);
    if (sameIntervalPeriod(requested, barDate, intervalKind)) {
      return {
        index,
        requestedDate: requestedValue,
        resolvedDate: barValue,
        exact: true,
        direction: "exact"
      };
    }
    const comparison = compareDateOnly(barDate, requested);
    if (comparison < 0) lastBefore = index;
    if (comparison > 0 && firstAfter < 0) firstAfter = index;
  }

  const index = firstAfter >= 0 ? firstAfter : lastBefore;
  if (index < 0) return null;
  const resolved = timestampToDateOnly(timestamps[index], timeZone);
  return {
    index,
    requestedDate: requestedValue,
    resolvedDate: formatDateOnly(resolved),
    exact: false,
    direction: firstAfter >= 0 ? "after" : "before"
  };
}

export function dateRangeForTimestamps(timestamps, timeZone) {
  const length = Number(timestamps?.length) || 0;
  let start = null;
  let end = null;
  for (let index = 0; index < length; index += 1) {
    const date = timestampToDateOnly(timestamps[index], timeZone);
    if (!date) continue;
    const value = formatDateOnly(date);
    start ||= value;
    end = value;
  }
  return start && end ? { start, end } : null;
}

export function isDateInRanges(date, ranges = []) {
  const value = formatDateOnly(date);
  return Boolean(value) && ranges.some(
    (range) => value >= String(range.start || "") && value <= String(range.end || "")
  );
}

export function isEditableTarget(target) {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(target.closest(
    "input, textarea, select, [contenteditable]:not([contenteditable='false']), [role='textbox']"
  ));
}

export function isGoToDateShortcut(event) {
  return !event?.defaultPrevented
    && !event?.altKey
    && !event?.shiftKey
    && Boolean(event?.ctrlKey || event?.metaKey)
    && String(event?.key || "").toLowerCase() === "g"
    && !isEditableTarget(event?.target);
}

function sameIntervalPeriod(requested, barDate, intervalKind) {
  if (intervalKind === "year") return requested.year === barDate.year;
  if (intervalKind === "month") {
    return requested.year === barDate.year && requested.month === barDate.month;
  }
  if (intervalKind === "week") {
    return dateOnlyToOrdinal(requested) - dayOfWeek(requested)
      === dateOnlyToOrdinal(barDate) - dayOfWeek(barDate);
  }
  return compareDateOnly(requested, barDate) === 0;
}

function dateIntervalKind(timeframe) {
  const value = String(timeframe || "").trim();
  if (/^\d+Y$/.test(value)) return "year";
  if (/^\d+M$/.test(value)) return "month";
  if (/^\d+W$/i.test(value)) return "week";
  return "day";
}

function dateOnlyToOrdinal(date) {
  const parsed = parseDateOnly(formatDateOnly(date));
  if (!parsed) throw new TypeError("A valid date-only value is required.");
  let year = parsed.year;
  const month = parsed.month;
  const day = parsed.day;
  year -= month <= 2 ? 1 : 0;
  const era = floorDivide(year, 400);
  const yearOfEra = year - era * 400;
  const adjustedMonth = month + (month > 2 ? -3 : 9);
  const dayOfYear = floorDivide(153 * adjustedMonth + 2, 5) + day - 1;
  const dayOfEra = yearOfEra * 365 + floorDivide(yearOfEra, 4)
    - floorDivide(yearOfEra, 100) + dayOfYear;
  return era * 146097 + dayOfEra - 719468;
}

function ordinalToDateOnly(ordinal) {
  let value = ordinal + 719468;
  const era = floorDivide(value, 146097);
  const dayOfEra = value - era * 146097;
  const yearOfEra = floorDivide(
    dayOfEra - floorDivide(dayOfEra, 1460) + floorDivide(dayOfEra, 36524)
      - floorDivide(dayOfEra, 146096),
    365
  );
  let year = yearOfEra + era * 400;
  const dayOfYear = dayOfEra - (
    365 * yearOfEra + floorDivide(yearOfEra, 4) - floorDivide(yearOfEra, 100)
  );
  const adjustedMonth = floorDivide(5 * dayOfYear + 2, 153);
  const day = dayOfYear - floorDivide(153 * adjustedMonth + 2, 5) + 1;
  const month = adjustedMonth + (adjustedMonth < 10 ? 3 : -9);
  year += month <= 2 ? 1 : 0;
  if (year < 1 || year > 9999) throw new RangeError("The date is outside the supported range.");
  return { year, month, day };
}

function datePartsFormatter(timeZone) {
  const key = String(timeZone || "UTC");
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: key,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
    formatterCache.set(key, formatter);
  }
  return formatter;
}

function pad(value, length) {
  return String(value).padStart(length, "0");
}

function floorDivide(value, divisor) {
  return Math.floor(value / divisor);
}

function modulo(value, divisor) {
  return ((value % divisor) + divisor) % divisor;
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}
